/**
 * Single source of truth for the Daily Vehicle Check questions.
 *
 * Keys are the vehicle_daily_checks column names, so they are stable IDs —
 * company settings store keys only (never wording). Used by the daily check
 * screen, the company settings toggles and the driver access gate.
 *
 * NOTE: this is the DAILY check list. VEHICLE_INSPECTION_ITEMS in
 * VehicleChecksStep.tsx is the separate POC/POD list and is not affected.
 */

export type DailyCheckSectionIcon =
  | "lights" | "tyres" | "brakes" | "mirrors" | "coupling"
  | "numberPlates" | "fluids" | "controls" | "safety" | "bodywork";

export interface DailyCheckQuestion { key: string; label: string }
export interface DailyCheckSectionDef {
  title: string;
  icon: DailyCheckSectionIcon;
  items: readonly DailyCheckQuestion[];
}

export const DAILY_CHECK_SECTIONS: readonly DailyCheckSectionDef[] = [
  { title: "Lights", icon: "lights", items: [
    { key: "lights_front", label: "Front lights working" },
    { key: "lights_rear", label: "Rear lights working" },
    { key: "lights_indicators", label: "Indicators working" },
    { key: "lights_brake", label: "Brake lights working" },
    { key: "lights_hazard", label: "Hazard lights working" },
  ] },
  { title: "Tyres", icon: "tyres", items: [
    { key: "tyres_condition", label: "Tyre condition acceptable" },
    { key: "tyres_pressure", label: "Tyre pressure checked" },
    { key: "tyres_tread_depth", label: "Tread depth adequate" },
  ] },
  { title: "Brakes", icon: "brakes", items: [
    { key: "brakes_condition", label: "Brake condition satisfactory" },
    { key: "brakes_operation", label: "Brakes operate correctly" },
    { key: "handbrake", label: "Handbrake works correctly" },
  ] },
  { title: "Mirrors", icon: "mirrors", items: [
    { key: "mirrors_condition", label: "Mirrors in good condition" },
    { key: "mirrors_adjustment", label: "Mirrors properly adjusted" },
  ] },
  { title: "Coupling & Trailer", icon: "coupling", items: [
    { key: "coupling_condition", label: "Coupling in good condition" },
    { key: "coupling_secure", label: "Coupling securely attached" },
    { key: "trailer_lights", label: "Trailer lights working" },
    { key: "trailer_tyres", label: "Trailer tyres acceptable" },
    { key: "trailer_brakes", label: "Trailer brakes working" },
    { key: "trailer_coupling", label: "Trailer coupling secure" },
    { key: "load_straps", label: "Load straps in good condition" },
  ] },
  { title: "Number Plates & Load", icon: "numberPlates", items: [
    { key: "number_plates_front", label: "Front number plate visible" },
    { key: "number_plates_rear", label: "Rear number plate visible" },
    { key: "load_security", label: "Load secure (if applicable)" },
  ] },
  { title: "Fluid Levels", icon: "fluids", items: [
    { key: "fluid_levels_oil", label: "Engine oil level" },
    { key: "fluid_levels_coolant", label: "Coolant level" },
    { key: "fluid_levels_brake", label: "Brake fluid level" },
    { key: "fluid_levels_washer", label: "Washer fluid level" },
  ] },
  { title: "Controls & Visibility", icon: "controls", items: [
    { key: "horn", label: "Horn works" },
    { key: "wipers_condition", label: "Wipers in good condition" },
    { key: "wipers_operation", label: "Wipers operate correctly" },
    { key: "seatbelts", label: "Seatbelts working" },
    { key: "windscreen_condition", label: "Windscreen clear & undamaged" },
  ] },
  { title: "Safety Equipment", icon: "safety", items: [
    { key: "fire_extinguisher", label: "Fire extinguisher present" },
    { key: "first_aid_kit", label: "First aid kit present" },
    { key: "reflective_jacket", label: "Reflective jacket present" },
    { key: "warning_triangle", label: "Warning triangle present" },
  ] },
  { title: "Bodywork", icon: "bodywork", items: [
    { key: "bodywork_condition", label: "Bodywork in acceptable condition" },
  ] },
] as const;

export const ALL_DAILY_CHECK_KEYS: readonly string[] =
  DAILY_CHECK_SECTIONS.flatMap((s) => s.items.map((i) => i.key));

export const NO_QUESTIONS_SELECTED_MESSAGE = "Please select at least one Daily Vehicle Check question.";
export const DAILY_CHECK_SAVE_FAILED_MESSAGE = "Your Daily Vehicle Check could not be saved. Please try again.";

/**
 * Resolve the stored company selection. NULL/undefined = every question
 * (backward-compatible default so existing companies never lose checks).
 * Unknown keys are ignored. An explicit empty list resolves to every question
 * too, so a bad row can never produce a zero-question check.
 */
export function resolveEnabledDailyCheckKeys(stored: readonly string[] | null | undefined): string[] {
  if (!stored) return [...ALL_DAILY_CHECK_KEYS];
  const set = new Set(stored);
  const keys = ALL_DAILY_CHECK_KEYS.filter((k) => set.has(k));
  return keys.length > 0 ? keys : [...ALL_DAILY_CHECK_KEYS];
}

/** Sections containing only enabled questions; empty sections removed. */
export function filterDailyCheckSections(enabledKeys: readonly string[]): DailyCheckSectionDef[] {
  const set = new Set(enabledKeys);
  return DAILY_CHECK_SECTIONS
    .map((s) => ({ ...s, items: s.items.filter((i) => set.has(i.key)) }))
    .filter((s) => s.items.length > 0);
}

/** Admin-side validation. Returns an error message or null. */
export function validateDailyCheckSettings(requireChecks: boolean, enabledKeys: readonly string[]): string | null {
  if (!requireChecks) return null;
  const valid = enabledKeys.filter((k) => ALL_DAILY_CHECK_KEYS.includes(k));
  return valid.length === 0 ? NO_QUESTIONS_SELECTED_MESSAGE : null;
}

/** Value persisted to company_settings: NULL when every question is on. */
export function toStoredDailyCheckKeys(enabledKeys: readonly string[]): string[] | null {
  const valid = ALL_DAILY_CHECK_KEYS.filter((k) => enabledKeys.includes(k));
  return valid.length === ALL_DAILY_CHECK_KEYS.length ? null : valid;
}

/** Today's operational date in UK time (YYYY-MM-DD). Calendar day, not rolling 24h. */
export function getUKToday(now: Date = new Date()): string {
  return now.toLocaleDateString("en-CA", { timeZone: "Europe/London" });
}

/** Pages a locked driver can still reach (the check itself, account, help). */
export const DAILY_CHECK_GATE_ALLOWED_PATHS = [
  "/vehicle-daily-checks",
  "/settings",
  "/driver-settings",
  "/more",
  "/help-guides",
  "/feedback",
  "/report-problem",
  "/delete-account",
] as const;

export function isPathAllowedWhileLocked(pathname: string): boolean {
  return DAILY_CHECK_GATE_ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

/**
 * Pure gate decision. Only driver users are ever blocked; admins, owners and
 * super admins are never blocked. Locked while roles/settings still load so a
 * driver can't slip through, except non-drivers who are never locked.
 */
export function isDailyCheckGateLocked(input: {
  requireChecks: boolean;
  isDriver: boolean;
  isAdmin: boolean;
  checkDoneToday: boolean;
}): boolean {
  if (!input.isDriver || input.isAdmin) return false;
  return input.requireChecks && !input.checkDoneToday;
}
